import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { WarningIcon, CartIcon, AccountIcon } from '../../../public/icons';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import paymentService from '../../../services/paymentService';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { openModal } = useModal();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [expandedItem, setExpandedItem] = useState(null);

  const [form, setForm] = useState({
    email: '',
    phone: '+234 ',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    country: 'Nigeria',
    deliveryNote: ''
  });

  const shippingFee = 5000;
  const memoryIncluded = 30000;

  /* ───────── Load cart ───────── */
  useEffect(() => {
    try {
      const raw = localStorage.getItem('memoryMagnetsCart');
      const parsed = raw ? JSON.parse(raw) : [];
      setItems(parsed);
    } catch (err) {
      console.error('Failed to load cart:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ───────── Prefill from logged-in user ───────── */
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone ? user.phone : prev.phone
      }));
    }
  }, [user]);

  /* ───────── Totals ───────── */
  const subtotal = items.reduce(
    (sum, item) => sum + (item.lineTotal || item.price * item.quantity || 0),
    0
  );
  const total = subtotal + memoryIncluded + shippingFee;

  /* ───────── Handlers ───────── */
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.email.trim()) next.email = 'Email is required';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 10)
      next.phone = 'Enter a valid phone number';
    if (!form.firstName.trim()) next.firstName = 'First name is required';
    if (!form.lastName.trim()) next.lastName = 'Last name is required';
    if (!form.address.trim()) next.address = 'Address is required';
    if (!form.city.trim()) next.city = 'City is required';
    if (!form.state.trim()) next.state = 'State is required';
    if (!form.country.trim()) next.country = 'Country is required';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCompleteOrder = async () => {
    if (items.length === 0) return;
    if (!isAuthenticated) return;
    if (!validate()) return;

    setSubmitting(true);
    try {
      const orderPayload = {
        items: items.map((it) => ({
          productId: it.id || null,
          slug: it.slug || null,
          title: it.title,
          description: it.description || '',
          image: it.image || '',
          isCustom: !!it.isCustom,
          unitPrice: it.price,
          quantity: it.quantity,
          lineTotal: it.lineTotal || it.price * it.quantity,

          // 🟢 Memory Magnet
          magnetId: it.magnetId || null,
          magnetName: it.magnetName || '',
          collectionName: it.collectionName || '',

          // 🟢 Audio Gift
          giftItem: it.giftItem || null,
          giftItemLabel: it.giftItemLabel || '',
          messageTitle: it.messageTitle || '',
          revealDate: it.revealDate || null,
          recipient: it.recipient || null,
          audioSource: it.audioSource || null,

          customization: it.customization || {},
          audioFile: it.audioFile || null,
          audioUrl: it.audioUrl || '',
          personalizedMessage: it.personalizedMessage || '',
          magnets: it.customization?.magnets || []
        })),
        customer: {
          email: form.email.trim(),
          phone: form.phone.trim(),
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim()
        },
        delivery: {
          address: form.address.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          country: form.country,
          deliveryNote: form.deliveryNote.trim()
        },
        subtotal,
        memoryIncluded,
        shippingFee,
        total
      };

      const res = await paymentService.initialize(null, orderPayload);

      if (!res.success || !res.authorizationUrl) {
        throw new Error(res.message || 'Could not start payment');
      }

      localStorage.setItem(
        'kapta_pending_order',
        JSON.stringify({
          orderId: res.orderId,
          orderNumber: res.orderNumber,
          reference: res.reference
        })
      );

      localStorage.removeItem('memoryMagnetsCart');
      window.dispatchEvent(new Event('cartUpdated'));

      window.location.href = res.authorizationUrl;
    } catch (err) {
      console.error('Order failed:', err);
      alert(
        err.response?.data?.message ||
          err.message ||
          'Failed to start payment. Please try again.'
      );
      setSubmitting(false);
    }
  };

  /* ───────── Per-item detail rows ───────── */
  const renderItemDetails = (item) => {
    const rows = [];
    const c = item.customization || {};

    // 🟢 Memory Magnet specifics
    if (item.magnetName) {
      rows.push({ label: 'Magnet', value: item.magnetName });
    }
    if (item.collectionName) {
      rows.push({ label: 'Collection', value: item.collectionName });
    }

    if (item.isCustom) rows.push({ label: 'Custom Gift Box', value: 'Yes' });

    // Customization set size (Frame It Forward / Legacy)
    if (c.setSize) rows.push({ label: 'Set', value: c.setSize });
    if (c.magnetType)
      rows.push({
        label: 'Magnet Type',
        value: c.magnetType === 'pre-curated' ? 'Pre-curated' : 'Uploaded Photos'
      });
    if (c.magnetSetCount)
      rows.push({ label: 'Magnet Sets', value: c.magnetSetCount });
    if (c.frameCount) rows.push({ label: 'Frames', value: c.frameCount });
    if (c.color) rows.push({ label: 'Colour', value: c.color });

    if (c.extras?.selectedExtras?.length) {
      rows.push({
        label: 'Extras',
        value: c.extras.selectedExtras.join(', ')
      });
    }
    if (c.extras?.selectedAudioItem) {
      rows.push({
        label: 'Audio Gift Item',
        value:
          c.extras.selectedAudioItem === 'teddy-bear'
            ? 'Teddy Bear'
            : 'Wooden Alarm Clock'
      });
    }

    // Future gifts
    if (c.futureGift?.futureGifts?.length) {
      rows.push({
        label: 'Future Gifts',
        value: `${c.futureGift.futureGifts.length} scheduled`
      });
      c.futureGift.futureGifts.forEach((fg, i) => {
        rows.push({
          label: `  • ${fg.title || `Gift ${i + 1}`}`,
          value: `${fg.deliveryDate} • ${fg.giftType}`
        });
      });
    }

    // 🟢 Audio Gift specifics
    if (item.giftItemLabel) {
      rows.push({ label: 'Gift Item', value: item.giftItemLabel });
    }
    if (item.audioSource) {
      rows.push({ label: 'Audio Source', value: item.audioSource });
    }
    if (item.audioFile?.name) {
      rows.push({ label: 'Audio', value: item.audioFile.name });
    }
    if (item.audioUrl) {
      rows.push({ label: 'Audio URL', value: item.audioUrl });
    }

    if (item.personalizedMessage) {
      rows.push({
        label: 'Message',
        value:
          item.personalizedMessage.length > 60
            ? item.personalizedMessage.slice(0, 60) + '…'
            : item.personalizedMessage
      });
    }
    if (c.magnets?.length) {
      rows.push({ label: 'Magnets Added', value: `${c.magnets.length}` });
    }
    if (item.quantity) {
      rows.push({ label: 'Quantity', value: item.quantity });
    }

    return rows;
  };

  /* ───────── Loading ───────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146]" />
      </div>
    );
  }

  /* ───────── Empty bag ───────── */
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex flex-col items-center justify-center px-4">
        <CartIcon size={48} color="#6B635D" />
        <h2 className="text-xl font-medium text-[#1C2A46] mt-4 mb-2">
          Your bag is empty
        </h2>
        <p className="text-sm text-[#6B635D] mb-6">
          Add something meaningful before checking out.
        </p>
        <Link
          to="/shop"
          className="bg-[#D2A146] text-white px-6 py-3 font-medium hover:bg-[#C49660] transition-colors"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  /* ───────── Not logged in ───────── */
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FCF7EE]">
        <div className="container mx-auto max-w-2xl px-4 sm:px-6 pt-28 sm:pt-32 pb-16">
          <div className="bg-white border border-[#DBDBDB] p-8 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#F4E7F1] flex items-center justify-center mx-auto mb-6">
              <AccountIcon size={28} color="#55000C" />
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-[#1C2A46] mb-3">
              Sign in to complete your order
            </h1>
            <p className="text-sm sm:text-base text-[#6B635D] max-w-md mx-auto mb-8 leading-relaxed">
              We use your account to save your delivery details, send order
              updates, and let you track your memories over time.
            </p>

            <div className="space-y-3 max-w-sm mx-auto">
              <button
                onClick={() =>
                  openModal('signin', { redirectTo: location.pathname })
                }
                className="w-full py-3 bg-[#D2A146] text-white font-medium hover:bg-[#C49660] transition-colors"
              >
                Sign In
              </button>
             
            </div>

            <p className="text-xs text-[#6B635D] mt-6">
              Your bag will be saved while you sign in.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── Logged in — checkout ───────── */
  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 pt-28 sm:pt-32 pb-16 text-left">
        <div className="mb-10 text-left">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1C2A46] mb-2 text-left">
            Checkout
          </h1>
          <p className="text-sm sm:text-base text-[#6B635D] text-left">
            You’re just a few steps away from completing your order.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: Form */}
          <div className="lg:col-span-3 space-y-10 text-left">
            <section className="text-left">
              <h2 className="text-xl font-medium text-[#1C2A46] mb-5 text-left">
                Contact Information
              </h2>

              <div className="mb-4 text-left">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full p-4 bg-[#F5F5F5] border ${
                    errors.email ? 'border-red-400' : 'border-[#E4E4E4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="flex gap-2 mb-3">
                <div className="w-20 p-4 bg-[#F5F5F5] border border-[#E4E4E4] text-[#1C2A46] text-center font-light">
                  +234
                </div>
                <input
                  type="tel"
                  placeholder="Enter your Phone number"
                  value={form.phone.replace('+234 ', '')}
                  onChange={(e) =>
                    handleChange('phone', '+234 ' + e.target.value)
                  }
                  className={`flex-1 p-4 bg-[#F5F5F5] border ${
                    errors.phone ? 'border-red-400' : 'border-[#E4E4E4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                />
              </div>
              {errors.phone && (
                <p className="mb-3 text-xs text-red-600 text-left">
                  {errors.phone}
                </p>
              )}

              <p className="text-xs text-[#6B635D] flex items-start gap-2 text-left">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[#6B635D] text-[10px] mt-0.5 flex-shrink-0">
                  i
                </span>
                We’ll use this to send order updates and access your memories.
              </p>
            </section>

            <section className="text-left">
              <h2 className="text-xl font-medium text-[#1C2A46] mb-5 text-left">
                Delivery Details
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="text-left">
                  <input
                    type="text"
                    placeholder="*Enter first name"
                    value={form.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    className={`w-full p-4 bg-[#F5F5F5] border ${
                      errors.firstName ? 'border-red-400' : 'border-[#E4E4E4]'
                    } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-red-600 text-left">
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div className="text-left">
                  <input
                    type="text"
                    placeholder="*Enter last name"
                    value={form.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className={`w-full p-4 bg-[#F5F5F5] border ${
                      errors.lastName ? 'border-red-400' : 'border-[#E4E4E4]'
                    } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-red-600 text-left">
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4 text-left">
                <input
                  type="text"
                  placeholder="Enter your address"
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className={`w-full p-4 bg-[#F5F5F5] border ${
                    errors.address ? 'border-red-400' : 'border-[#E4E4E4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                />
                {errors.address && (
                  <p className="mt-1 text-xs text-red-600 text-left">
                    {errors.address}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="relative text-left">
                  <input
                    type="text"
                    placeholder="City"
                    value={form.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className={`w-full p-4 bg-[#F5F5F5] border ${
                      errors.city ? 'border-red-400' : 'border-[#E4E4E4]'
                    } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                  />
                  {errors.city && (
                    <p className="mt-1 text-xs text-red-600 text-left">
                      {errors.city}
                    </p>
                  )}
                </div>
                <div className="relative text-left">
                  <input
                    type="text"
                    placeholder="State"
                    value={form.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className={`w-full p-4 bg-[#F5F5F5] border ${
                      errors.state ? 'border-red-400' : 'border-[#E4E4E4]'
                    } focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light text-left`}
                  />
                  {errors.state && (
                    <p className="mt-1 text-xs text-red-600 text-left">
                      {errors.state}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4 text-left">
                <select
                  value={form.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  className="w-full p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent text-[#1C2A46] appearance-none text-left"
                >
                  <option value="Nigeria">Nigeria</option>
                  <option value="Ghana">Ghana</option>
                  <option value="Kenya">Kenya</option>
                  <option value="South Africa">South Africa</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Canada">Canada</option>
                </select>
              </div>

              <div className="mb-4 text-left">
                <textarea
                  placeholder="Delivery note"
                  value={form.deliveryNote}
                  onChange={(e) => handleChange('deliveryNote', e.target.value)}
                  rows={5}
                  className="w-full p-4 bg-[#F5F5F5] border border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent placeholder:text-[#6B635D] placeholder:font-light resize-none text-left"
                />
              </div>
            </section>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-2 text-left">
            <div className="lg:sticky lg:top-28 text-left">
              <h2 className="text-xl font-medium text-[#1C2A46] mb-6 text-left">
                Order Summary
              </h2>

              <div className="space-y-6 pb-6 border-b border-[#DBDBDB] text-left">
                {items.map((item, idx) => {
                  const details = renderItemDetails(item);
                  const isExpanded = expandedItem === idx;

                  return (
                    <div key={item.cartItemId || idx} className="text-left">
                      <div className="flex gap-4 items-center text-left">
                        <div className="w-[129px] h-[162px] flex-shrink-0 bg-[#F5F5F5] overflow-hidden">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="w-full h-[162px] object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <h3 className="text-base font-medium text-[#1C2A46] truncate text-left">
                            {item.title}
                          </h3>
                          {item.description && (
                            <p className="text-xs italic text-[#6B635D] mt-0.5 line-clamp-2 text-left">
                              {item.description}
                            </p>
                          )}
                          <p className="text-xs text-[#6B635D] mt-1 text-left">
                            Qty: {item.quantity}
                          </p>
                          <p className="text-sm font-medium text-[#1C2A46] mt-1 text-left">
                            ₦
                            {(
                              item.lineTotal ||
                              item.price * item.quantity
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {details.length > 0 && (
                        <button
                          onClick={() =>
                            setExpandedItem(isExpanded ? null : idx)
                          }
                          className="mt-2 text-xs text-[#D2A146] hover:text-[#C49660] underline text-left"
                        >
                          {isExpanded ? 'Hide details' : 'View details'}
                        </button>
                      )}

                      {isExpanded && details.length > 0 && (
                        <div className="mt-3 bg-[#F5F5F5] border border-[#E4E4E4] p-3 space-y-1.5 text-left">
                          {details.map((row, i) => (
                            <div
                              key={i}
                              className="flex justify-between gap-4 text-xs text-left"
                            >
                              <span className="text-[#6B635D] text-left">
                                {row.label}
                              </span>
                              <span className="text-[#1C2A46] text-right break-words max-w-[60%]">
                                {row.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {idx < items.length - 1 && (
                        <div className="mt-5 border-t border-[#DBDBDB]" />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3 py-6 text-left">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B635D] text-left">Subtotal</span>
                  <span className="text-[#1C2A46]">
                    ₦{subtotal.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B635D] flex items-center gap-2 text-left">
                    Memory Included
                    <WarningIcon className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-[#1C2A46]">
                    ₦{memoryIncluded.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B635D] text-left">Shipping Fee</span>
                  <span className="text-[#1C2A46]">
                    ₦{shippingFee.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="border-t border-[#DBDBDB] pt-4 flex justify-between items-center mb-6">
                <span className="text-base font-medium text-[#1C2A46] text-left">
                  Total
                </span>
                <span className="text-lg font-medium text-[#1C2A46]">
                  ₦{total.toLocaleString()}
                </span>
              </div>

              <button
                onClick={handleCompleteOrder}
                disabled={submitting}
                className={`w-full py-4 font-medium transition-colors flex items-center justify-center gap-2 ${
                  submitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {submitting ? 'Redirecting to Paystack…' : 'Complete Order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;